import type { CSSProperties } from "react";
import { Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { HERO_SLIDES, type HeroSlide } from "@/hooks/useHeroCarousel";

interface HeroSlideControlsProps {
  labels: Record<HeroSlide, string>;
  groupLabel: string;
  pauseLabel: string;
  playLabel: string;
  /** Formats the chip text for the slide that comes next. */
  nextLabel: (label: string) => string;
  viewLabel: string;
  index: number;
  flowStep: number;
  flowSteps: number;
  autoplay: boolean;
  playing: boolean;
  reducedMotion: boolean;
  clockKey: string;
  clockMs: number;
  onTick: () => void;
  onSelect: (index: number) => void;
  onToggle: () => void;
}

/**
 * Slide buttons with a progress line under the active one. The hidden clock is
 * a CSS animation whose end advances the carousel, so pausing it (hover, off
 * screen) freezes both the line and the timer at the same point.
 *
 * On desktop the video slide shows only the compact chip (CSS swaps it in), so
 * no button panel floats over the full-bleed video.
 */
export function HeroSlideControls({
  labels,
  groupLabel,
  pauseLabel,
  playLabel,
  nextLabel,
  viewLabel,
  index,
  flowStep,
  flowSteps,
  autoplay,
  playing,
  reducedMotion,
  clockKey,
  clockMs,
  onTick,
  onSelect,
  onToggle,
}: HeroSlideControlsProps) {
  const timed: CSSProperties = {
    animationDuration: `${clockMs}ms`,
    animationPlayState: playing ? "running" : "paused",
  };
  const activeSlide = HERO_SLIDES[index];
  const nextIndex = (index + 1) % HERO_SLIDES.length;
  const toggleLabel = autoplay ? pauseLabel : playLabel;

  return (
    <div className="hero-slide-controls" role="group" aria-label={groupLabel} data-testid="hero-slide-controls">
      {HERO_SLIDES.map((slide, i) => {
        const isActive = i === index;
        const flowFill = `${((flowStep + 1) / Math.max(flowSteps, 1)) * 100}%`;
        return (
          <button
            key={slide}
            type="button"
            className={cn("hero-slide-tab", isActive && "is-active", slide === "video" && "is-video")}
            aria-pressed={isActive}
            aria-controls={`hero-slide-${slide}`}
            onClick={() => onSelect(i)}
            data-testid={`hero-slide-tab-${slide}`}
          >
            <span>{labels[slide]}</span>
            <span className="hero-slide-progress" aria-hidden="true">
              {isActive && slide === "flow" && <span style={{ width: flowFill }} />}
              {isActive && slide !== "flow" && autoplay && (
                <span key={clockKey} className="is-timed" style={timed} />
              )}
              {isActive && slide !== "flow" && !autoplay && <span style={{ width: "100%" }} />}
            </span>
          </button>
        );
      })}

      {!reducedMotion && (
        <button
          type="button"
          className="hero-slide-toggle"
          onClick={onToggle}
          aria-label={toggleLabel}
          title={toggleLabel}
          data-testid="hero-slide-toggle"
        >
          {autoplay ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </button>
      )}

      <div className="hero-slide-chip" data-testid="hero-slide-chip">
        <span className="hero-slide-chip-dots" aria-hidden="true">
          {HERO_SLIDES.map((slide, i) => (
            <b key={slide} className={cn(i === index && "is-active")} />
          ))}
        </span>
        <span>{nextLabel(labels[HERO_SLIDES[nextIndex]])}</span>
        <button type="button" onClick={() => onSelect(nextIndex)} data-testid="hero-slide-chip-next">
          {viewLabel} →
        </button>
        {!reducedMotion && (
          <button
            type="button"
            className="hero-slide-chip-toggle"
            onClick={onToggle}
            aria-label={toggleLabel}
            title={toggleLabel}
          >
            {autoplay ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          </button>
        )}
      </div>

      {autoplay && (
        <span
          key={`${activeSlide}-${clockKey}`}
          className="hero-slide-clock"
          style={timed}
          onAnimationEnd={onTick}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
